import React, { useCallback, useEffect, useState, useMemo } from 'react';

import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  useMediaQuery,
  ModalFooter,
  HStack,
  Button,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import ComponentsTable from './ComponentsTable';
import { FlattenedComponent } from './types';

type ModalCustomCompositionMassUpdate = ModalRootProps & {
  data: {
    budgetId: number;
  };
};

const modalMargin = 120;
const modalMobileFix = 100;

const ModalCustomCompositionMassUpdate: React.FC<ModalCustomCompositionMassUpdate> =
  ({ handleClose, onConfirm, data: { budgetId }, ...restProps }) => {
    const [isMobile] = useMediaQuery('(max-width: 728px)');

    const [components, setComponents] = useState<FlattenedComponent[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const getData = useCallback(
      async (shouldLoad = true) => {
        if (shouldLoad) {
          setLoading(true);
          setComponents([]);
        }

        try {
          const response = await api.get(`/budget/${budgetId}/component`, {
            params: {
              // sort: 'compositions.sorteable',
            },
          });

          const componentsData = response.data;
          setComponents(componentsData.data);
        } catch (err) {
          setComponents([]);

          toast({
            status: 'error',
            description:
              err.response?.data?.message ||
              'Houve um erro ao buscar os itens do orçamento',
          });
        } finally {
          setLoading(false);
        }
      },
      [budgetId],
    );

    useEffect(() => {
      getData();
    }, [getData]);

    const customCompositions = useMemo(() => {
      return components.filter((c) => c.type === 'composition_custom');
    }, [components]);

    const handleEditCustomComposition = useCallback(
      async (id, price) => {
        setSaving(true);

        try {
          await api.put(`budget/${budgetId}/component/${id}`, {
            type: 'composition_custom',
            component: {
              ...price,
            },
          });

          toast({
            description: 'Item salvo com sucesso!',
            status: 'success',
          });

          getData(false);
        } catch (err) {
          toast({
            description:
              err?.response?.data?.message || 'Houve um erro ao salvar o item!',
            status: 'error',
          });
        } finally {
          setSaving(false);
        }
      },
      [budgetId, getData],
    );

    const handleFinishUpdating = (): void => {
      if (onConfirm) onConfirm();
      handleClose();
    };

    return (
      <Modal {...restProps} scrollBehavior="inside" closeOnEsc={false}>
        <ModalOverlay />
        <ModalContent
          sx={{
            width: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            maxWidth: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            height: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            maxHeight: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
          }}
        >
          <ModalHeader>Editar serviços a preço informado</ModalHeader>

          <ModalBody>
            <ComponentsTable
              data={customCompositions}
              onEditCustomComposition={handleEditCustomComposition}
              loading={loading}
              saving={saving}
            />
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button colorScheme="green" onClick={handleFinishUpdating}>
                Confirmar alterações
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

export default ModalCustomCompositionMassUpdate;
